import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { environment } from 'src/environments/environment';
import packageJson from '../../../package.json';
import { CussService } from './cuss.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private static readonly CHUNK_RELOAD_KEY = 'kioskChunkReloadAt';
  private static readonly CHUNK_RELOAD_WINDOW = 30000;
  private sessionStart = Date.now();

  constructor(private injector: Injector) {}

  handleError(error: any): void {
    const newRelic = (window as any).newrelic;
    const errorToSend = error?.rejection || error?.originalError || error;

    // CI-22821: chunk loading failure → reload automático con guard anti-loop.
    if (this.isChunkLoadError(errorToSend)) {
      this.handleChunkError();
      return;
    }

    if (newRelic?.noticeError) {
      const customAttributes: Record<string, any> = {
        kioskId: this.getKioskId(),
        uiVersion: packageJson.version,
        environment: environment.production ? 'production' : environment.isCert ? 'cert' : 'dev',
        appName: environment.appName,
        currentRoute: window.location.hash || window.location.pathname,
        reservationCode: this.getReservationCode(),
        errorName: errorToSend?.name || 'Unknown',
        errorMessage: errorToSend?.message || String(errorToSend),
        errorStack: (errorToSend?.stack || '').substring(0, 1000),
        errorComponent: this.extractComponent(errorToSend?.stack),
        sessionDuration: Math.round((Date.now() - this.sessionStart) / 1000),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        memoryUsed: this.getMemoryUsage(),
        source: 'GlobalErrorHandler',
      };

      newRelic.noticeError(errorToSend, customAttributes);

      newRelic.addPageAction('KIOSK_UNHANDLED_ERROR', {
        ...customAttributes,
        isError: true,
      });
    }

    console.error('[KioskError]', error);
  }

  /**
   * CI-22821: ejecuta un reload si no hubo otro en los últimos 30 s.
   * Guard anti-loop con sessionStorage, siguiendo la convención del repo.
   *
   * CI-23100 / errores de chunk: expuesto para que las cargas lazy puedan
   * delegar la recuperación reutilizando el mismo guard anti-loop.
   */
  public handleChunkError(): void {
    try {
      const raw = sessionStorage.getItem(GlobalErrorHandler.CHUNK_RELOAD_KEY);
      const last = raw ? Number(raw) : 0;
      if (Date.now() - last < GlobalErrorHandler.CHUNK_RELOAD_WINDOW) {
        // Dentro de la ventana: no recargar para evitar loop infinito.
        return;
      }
      sessionStorage.setItem(GlobalErrorHandler.CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      // sessionStorage no disponible: se permite el reload de todos modos.
    }
    window.location.reload();
  }

  private getKioskId(): string {
    try {
      const cussService = this.injector.get(CussService);
      return cussService?.KioskID || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private getReservationCode(): string {
    try {
      const data = sessionStorage.getItem('reservation');
      if (data) {
        const reservation = JSON.parse(data);
        return reservation?.pnr || 'empty';
      }
    } catch {}
    return 'empty';
  }

  private extractComponent(stack: string): string {
    try {
      if (!stack) return 'unknown';
      const match = stack.match(/at (\w+Component)/);
      return match ? match[1] : 'unknown';
    } catch {}
    return 'unknown';
  }

  private getMemoryUsage(): string {
    try {
      const perf = performance as any;
      if (perf?.memory) {
        const usedMB = Math.round(perf.memory.usedJSHeapSize / 1048576);
        const totalMB = Math.round(perf.memory.totalJSHeapSize / 1048576);
        return `${usedMB}MB/${totalMB}MB`;
      }
    } catch {}
    return 'unavailable';
  }

  /**
   * CI-22821: detecta si el error es un fallo de carga de chunk lazy.
   */
  private isChunkLoadError(error: unknown): boolean {
    const message: string = (error as { message?: string })?.message || String(error ?? '');
    const name: string = (error as { name?: string })?.name || '';
    return (
      name === 'ChunkLoadError' ||
      /Loading chunk .+ failed/i.test(message) ||
      /Loading CSS chunk .+ failed/i.test(message)
    );
  }

}
